import { Alert, Table, Typography, theme } from 'antd';
import { useEffect, useState } from 'react';

import {
    BackgroundCheckScreeningStatus,
    BackgroundCheckScreeningStatusDto,
    BackgroundCheckScreeningsDto,
} from '@clh/api-client';
import { useApiClient } from '@clh/ui';

import Spinner from '../../spinner';

import { screeningStatusLabel } from './screening-status';

function StatusDot({ status }: { status: BackgroundCheckScreeningStatus }) {
    const { token } = theme.useToken();
    const colors: Record<BackgroundCheckScreeningStatus, string> = {
        NOT_STARTED: token.colorTextQuaternary,
        IN_PROGRESS: token.colorInfo,
        COMPLETE: token.colorSuccess,
        NEEDS_REVIEW: token.colorWarning,
        CANCELED: token.colorTextQuaternary,
    };
    const color = colors[status];

    return (
        <span
            style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                marginRight: 6,
                borderRadius: '50%',
                border: `1.5px solid ${color}`,
                background:
                    status === BackgroundCheckScreeningStatus.NotStarted
                        ? 'transparent'
                        : color,
            }}
        />
    );
}

function orderSummary({ orderedAt, screenings }: BackgroundCheckScreeningsDto) {
    const count = `${screenings.length} ${screenings.length === 1 ? 'item' : 'items'}`;

    return orderedAt
        ? `Ordered ${orderedAt.toLocaleDateString()} · ${count}`
        : count;
}

export function KarmaCheckScreenings({
    backgroundCheckId,
}: {
    backgroundCheckId: string;
}) {
    const api = useApiClient();
    const [order, setOrder] = useState<BackgroundCheckScreeningsDto>();
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        api?.backgroundCheckControllerListScreenings({ backgroundCheckId })
            .then(setOrder)
            .catch(() => setFailed(true));
    }, [api, backgroundCheckId]);

    if (failed) {
        return (
            <Alert
                type="error"
                showIcon
                message="Couldn't load screenings from KarmaCheck."
            />
        );
    }

    if (!order) {
        return <Spinner />;
    }

    return (
        <>
            <Typography.Text type="secondary">
                {orderSummary(order)}
            </Typography.Text>
            <Table<BackgroundCheckScreeningStatusDto>
                size="small"
                rowKey={(screening, index) => `${screening.serviceId}-${index}`}
                pagination={false}
                dataSource={order.screenings}
                locale={{ emptyText: 'No screenings in this order.' }}
                style={{ marginTop: 8 }}
                columns={[
                    { title: 'Item', dataIndex: 'name' },
                    {
                        title: 'Status',
                        render: (_val, screening) => (
                            <>
                                <StatusDot status={screening.status} />
                                {screeningStatusLabel(screening)}
                            </>
                        ),
                    },
                    {
                        title: 'Updated',
                        dataIndex: 'updatedAt',
                        render: (updatedAt: Date | null) =>
                            updatedAt?.toLocaleDateString() ?? '—',
                    },
                ]}
            />
        </>
    );
}
